<!-- =========================================================================================
    File Name: AgencyList.vue
    Description: Searching agency list.
========================================================================================== -->

<template>
  <div>
    <ais-instant-search
      :search-client="searchClient"
      index-name="ESODictionary"
      id="agolia-instance-agency-search"
    >
      <!-- AIS CONFIG -->
      <ais-configure :hits-per-page.camel="9" :facetFilters.camel="query" />

      <div class="algolia-header mb-4">
        <div class="flex md:items-end items-center justify-between flex-wrap">
          <!-- TOGGLE SIDEBAR BUTTON -->
          <feather-icon
            class="inline-flex lg:hidden cursor-pointer mr-4"
            icon="MenuIcon"
            @click.stop="toggleFilterSidebar"
          />
        </div>
      </div>

      <div id="algolia-content-container" class="relative clearfix">
        <vs-sidebar
          class="main-menu-sidebar items-no-padding vs-sidebar-rounded background-absolute"
          parent="#algolia-content-container"
          :click-not-close="clickNotClose"
          :hidden-background="clickNotClose"
          v-model="isFilterSidebarActive"
        >
          <div class="header-sidebar flex items-end justify-between" slot="header">
            <div class="logo flex items-center">
              <img :src="this.logo" alt="logo" width="100" class="mr-4" v-if="this.logo" />
              <!-- <span class="logo-text" v-if="this.title">{{ this.title }}</span> -->
              <feather-icon
                icon="XIcon"
                class="m-0 cursor-pointer"
                svg-classes="stroke-current"
                v-if="!clickNotClose"
                @click="toggleFilterSidebar"
              ></feather-icon>
            </div>
          </div>
          <div class="shadow-bottom"></div>
          <VuePerfectScrollbar
            ref="mainSidebarPs"
            class="scroll-area--main-sidebar pt-2"
            :settings="settings"
            @ps-scroll-y="psSectionScroll"
          >
            <div class="p-6 filter-container">
              <!-- Dictionary sections -->
              <h6 class="font-bold mb-4">Dictionary sections</h6>
              <ais-hierarchical-menu
                :attributes="[
                    'menu.lvl0',
                    'menu.lvl1',
                    'menu.lvl2',
                    'menu.lvl3',
                ]"
              >
                <div
                  slot-scope="{
                    items,
                    canToggleShowMore,
                    isShowingMore,
                    refine,
                    toggleShowMore,
                    createURL,
                  }"
                >
                  <hierarchical-menu-list :items="items" :refine="refine" :createURL="createURL" />
                </div>
              </ais-hierarchical-menu>

              <vs-divider />
              <h6 class="font-bold mb-4">Data Type</h6>
              <ais-refinement-list attribute="Data Type">
                <div
                  slot-scope="{
                      items,
                      isFromSearch,
                      refine,
                  }"
                >
                  <ul>
                    <li v-if="isFromSearch && !items.length">No results.</li>
                    <li
                      v-for="item in items"
                      :key="item.value"
                      class="mb-2 flex items-center justify-between"
                    >
                      <vs-checkbox
                        v-model="item.isRefined"
                        class="ml-0"
                        @click="refine(item.value)"
                      >{{ item.label }}</vs-checkbox>
                      <span>{{ item.count }}</span>
                    </li>
                  </ul>
                </div>
              </ais-refinement-list>
              <vs-divider />
              <ais-clear-refinements style="height: 300px;">
                <a
                  slot-scope="{ canRefine, refine, createURL }"
                  :href="createURL()"
                  @click.prevent="clearAllFilter(refine)"
                >Clear all filters</a>
              </ais-clear-refinements>
            </div>
          </VuePerfectScrollbar>
        </vs-sidebar>

        <!-- RIGHT COL -->
        <div :class="{'sidebar-spacer-with-margin': clickNotClose}">
          <!-- SEARCH BAR -->
          <ais-search-box>
            <div slot-scope="{ currentRefinement, isSearchStalled, refine }">
              <div class="relative mb-8">
                <!-- SEARCH INPUT -->
                <vs-input
                  class="w-full vs-input-shadow-drop vs-input-no-border d-theme-input-dark-bg"
                  placeholder="Search the dictionary"
                  v-model="currentRefinement"
                  @input="refine($event)"
                  @keyup.esc="refine('')"
                  size="large"
                />

                <!-- SEARCH LOADING -->
                <span :hidden="!isSearchStalled">Loading...</span>

                <!-- SEARCH ICON -->
                <div
                  slot="submit-icon"
                  class="absolute top-0 right-0 py-4 px-6"
                  v-show="!currentRefinement"
                >
                  <feather-icon icon="SearchIcon" svgClasses="h-6 w-6" />
                </div>

                <!-- CLEAR INPUT ICON -->
                <div
                  slot="reset-icon"
                  class="absolute top-0 right-0 py-4 px-6"
                  v-show="currentRefinement"
                >
                  <feather-icon
                    icon="XIcon"
                    svgClasses="h-6 w-6 cursor-pointer"
                    @click="refine('')"
                  />
                </div>
              </div>
            </div>
          </ais-search-box>
          <!-- Stats -->
          <ais-stats>
            <p
              slot-scope="{ hitsPerPage, nbPages, nbHits, page, processingTimeMS }"
              class="font-semibold md:block hidden"
            >{{ nbHits }} results found in {{ processingTimeMS }}ms</p>
          </ais-stats>
          <ais-current-refinements
            :class-names="{
                'ais-CurrentRefinements': 'current-refinement-wrapper',
            }"
            :included-attributes="['Data Type','Location Breadcrumb 1', 'Location Breadcrumb 2', 'Location Breadcrumb 3', 'Location Breadcrumb 4']"
          >
            <div slot-scope="{ items }" class="current-refinement-list">
              <div v-for="item in items" :key="item.attribute" class="current-refinement-list">
                {{ item.label }}:
                <div class="current-refinement-item">
                  <vs-chip
                    v-for="refinement in item.refinements"
                    :key="[
                            refinement.attribute,
                            refinement.type,
                            refinement.value,
                            refinement.operator
                        ].join(':')"
                    @click="item.refine(refinement)"
                    closable
                    color="primary"
                  >{{ refinement.label }}</vs-chip>
                </div>
              </div>
            </div>
          </ais-current-refinements>
          <div style="margin-bottom: 24px;">
            <vs-chip
              v-if="selectedBreadVal.length"
              @click="clearAllFilter(null)"
              closable
              color="#214c5f"
            >{{ selectedBreadVal }}</vs-chip>
          </div>
          <br />
          <!-- SEARCH RESULT -->
          <app-infinite-hits>
            <!-- LIST VIEW -->

            <template slot="item" slot-scope="{ item }">
              <div class="items-list-view">
                <item-list-view :item="item">
                  <template slot="breadcrumb-menu-item">
                    <nav aria-label="breadcrumb" class="vs-breadcrumb vs-align-left">
                      <ol class="vs-breadcrumb--ol">
                        <li>
                          <a
                            href="#"
                            class="router-link-active"
                            :class="key=='Location Breadcrumb 1'?'active': ''"
                            @click="changeQuery('Location Breadcrumb 1', item[ 'Location Breadcrumb 1'])"
                          >{{item[ "Location Breadcrumb 1"]}}</a>
                          <span
                            class="vs-breadcrum--separator"
                            v-if="item[ 'Location Breadcrumb 2'] !== ''"
                          >/</span>
                        </li>
                        <li>
                          <a
                            href="#"
                            :class="key=='Location Breadcrumb 2'?'active': ''"
                            @click="changeQuery('Location Breadcrumb 2', item[ 'Location Breadcrumb 2'])"
                          >{{item[ "Location Breadcrumb 2"]}}</a>
                          <span
                            class="vs-breadcrum--separator"
                            v-if="item[ 'Location Breadcrumb 3'] !== ''"
                          >/</span>
                        </li>
                        <li>
                          <a
                            href="#"
                            :class="key=='Location Breadcrumb 3'?'active': ''"
                            @click="changeQuery('Location Breadcrumb 3', item[ 'Location Breadcrumb 3'])"
                          >{{item[ "Location Breadcrumb 3"]}}</a>
                          <span
                            class="vs-breadcrum--separator"
                            v-if="item[ 'Location Breadcrumb 4'] !== ''"
                          >/</span>
                        </li>
                        <li>
                          <a
                            href="#"
                            :class="key=='Location Breadcrumb 4'?'active': ''"
                            @click="changeQuery('Location Breadcrumb 4', item[ 'Location Breadcrumb 4'])"
                          >{{item[ "Location Breadcrumb 4"]}}</a>
                        </li>
                      </ol>
                    </nav>
                  </template>
                  <!-- SLOT: ACTION BUTTONS -->
                  <template slot="action-buttons">
                    <div class="w-full">
                      <div class="w-full text-center">
                        <div
                          class="item-view-secondary-action-btn nemsis-label p-3 flex flex-grow items-center justify-center text-white"
                        >
                          <span class="text-sm font-semibold ml-2">NEMSIS 3 Element</span>
                        </div>
                        <a
                          v-if="item['NEMSIS 3 Element url'].length && item['NEMSIS 3 Element url'] !== null && item['NEMSIS 3 Element url'] != undefined"
                          :href="item['NEMSIS 3 Element url']"
                          target="_blank"
                          style="color: #19BC9C"
                        >{{item['NEMSIS 3 Element']}}</a>
                        <a v-else target="_blank" >{{item['NEMSIS 3 Element']}}</a>
                      </div>
                      <vs-divider position="center" style="margin: 4px 0 !important;"></vs-divider>
                      <div class="items-center justify-center text-center" style="display:grid;">
                        <vs-button
                          size="small"
                          color="#19BC9C"
                          @click="changeQuery('Data Type', item[ 'Data Type'])"
                          class="item-btn"
                        >{{item['Data Type']}}</vs-button>
                        <vs-button
                          size="small"
                          color="#214c5f"
                          @click="changeQuery('Data Entry Method', item[ 'Data Entry Method'])"
                          class="item-btn"
                        >{{item['Data Entry Method']}}</vs-button>
                      </div>
                    </div>
                  </template>
                </item-list-view>
              </div>
            </template>
          </app-infinite-hits>
        </div>
      </div>
    </ais-instant-search>
  </div>
</template>

<script>
import algoliasearch from "algoliasearch/lite";
import { agoliaSettings } from "../../configs/config";
import sidebarItems from "@/layouts/components/vx-sidebar/sidebarItems.js";
import AppInfiniteHits from "../components/InfiniteHits";
import HierarchicalMenuList from "../components/HierarchicalMenu";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
const logo = require("@/assets/images/logo/LOGO-Master.svg");
export default {
  components: {
    ItemListView: () => import("../components/ItemListView"),
    AppInfiniteHits: AppInfiniteHits,
    HierarchicalMenuList: HierarchicalMenuList,
    VuePerfectScrollbar: VuePerfectScrollbar
  },
  data() {
    return {
      searchClient: algoliasearch(
        agoliaSettings.agoliaAppId,
        agoliaSettings.searchAPIKey
      ),
      // Filter Sidebar
      isFilterSidebarActive: true,
      clickNotClose: true,
      logo: logo,
      query: "",
      sidebarItems: sidebarItems,
      windowWidth: window.innerWidth,
      title: "Dictionary",
      key: "",
      selectedBreadVal: "",
      currentItemView: "item-list-view",
      settings: {
        // perfectscrollbar settings
        maxScrollbarLength: 60,
        wheelSpeed: 1
        // swipeEasing: true
      },
      showShadowBottom: false
    };
  },
  computed: {},
  methods: {
    changeQuery(key, val) {
      this.key = key;
      this.selectedBreadVal = val;
      this.query = ["" + key + ":" + val + ""];
    },
    clearAllFilter(fun) {
      if (typeof fun == "function") {
        fun();
      }
      this.query = "";
      this.key = "";
      this.selectedBreadVal = "";
    },
    handleWindowResize(event) {
      this.windowWidth = event.currentTarget.innerWidth;
      this.setSidebarWidth();
    },
    setSidebarWidth() {
      if (this.windowWidth >= 992) {
        this.$store.dispatch("updateSidebarWidth", "default");
      } else if (this.windowWidth < 992) {
        this.$store.commit("TOGGLE_IS_SIDEBAR_ACTIVE", false);
        this.$store.dispatch("updateSidebarWidth", "no-sidebar");
        this.disableThemeTour = true;
      } else if (this.windowWidth < 992) {
        this.$store.dispatch("updateSidebarWidth", "reduced");
      } else {
        this.$store.commit("TOGGLE_IS_SIDEBAR_ACTIVE", true);
      }
      if (this.windowWidth < 992) {
        this.isFilterSidebarActive = this.clickNotClose = false;
      } else {
        this.isFilterSidebarActive = this.clickNotClose = true;
      }
    },

    // GRID VIEW - ACTIONS
    toggleFilterSidebar() {
      if (this.clickNotClose) return;
      this.isFilterSidebarActive = !this.isFilterSidebarActive;
    },
    psSectionScroll() {
      if (this.$refs.mainSidebarPs.$el.scrollTop > 0)
        this.showShadowBottom = true;
      else this.showShadowBottom = false;
    }
  },
  created() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.handleWindowResize);
    });
    this.setSidebarWidth();
  },
  beforeDestroy: function() {
    window.removeEventListener("resize", this.handleWindowResize);
  }
};
</script>

<style lang="scss">
.current-refinement-wrapper {
  margin-bottom: 15px;
}
.vs-sidebar.vs-sidebar-parent {
  position: fixed;
  max-width: 460px;
}
.btn-chip-class {
  min-height: 33px;
  min-width: 100px;
  font-size: 14px;
}
.stroke-current {
  color: #7367f0;
  width: 20px;
  height: 20px;
  margin-left: 230px;
}
.logo-text {
  font-size: 22px;
  font-weight: 600;
  color: #7367f0;
  -webkit-animation: fadein 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) 0s 1 normal
    forwards;
  animation: fadein 0.3s cubic-bezier(0.25, 0.8, 0.25, 1) 0s 1 normal forwards;
}
.current-refinement-list {
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
  -webkit-box-pack: start;
  -ms-flex-pack: start;
  justify-content: flex-start;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
}
.current-refinement-item {
  margin-left: 15px;
}
.nemsis-label {
  padding: 4px !important;
  font-size: 10px;
  background-color: lightgrey !important;
  font-weight: 300!important;
}
@media (min-width: 992px) {
  .vs-sidebar-rounded {
    .vs-sidebar {
      border-radius: 0.5rem;
    }
    .vs-sidebar--items {
      border-radius: 0.5rem;
    }
  }
}

@media (max-width: 992px) {
  #algolia-content-container {
    .vs-sidebar {
      position: absolute !important;
      float: none !important;
    }
  }
  .stroke-current {
    margin-left: 55px;
  }
}
a:not([href]) {
  color: #626262;
}
a:active,
a:visited,
a:hover,
a {
  color: #214c5f;
}
.vs-breadcrumb--ol a:focus,
.vs-breadcrumb--ol a:hover {
  color: #214c5f;
}
.vs-breadcrumb--ol .active {
  color: #214c5f;
}
.item-btn {
  margin: 4px;
  opacity: 1 !important;
}
</style>
